import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Sparkles, Repeat, Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const packs = [
  {
    id: "logo",
    name: "Pack Logo Essentiel",
    price: "699",
    suffix: "€",
    highlight: false,
    features: [
      "Logo",
      "Déclinaisons réseaux",
      "Adaptation couleurs",
      "Fichiers HD",
    ],
    bonus: "2 visuels offerts",
  },
  {
    id: "branding",
    name: "Pack Branding Complet",
    price: "1599",
    suffix: "€",
    prefix: "À partir de",
    highlight: true,
    badge: "Le plus choisi",
    features: [
      "Logo + déclinaisons",
      "Palette couleurs",
      "Typographies",
      "Charte graphique",
      "Univers visuel",
      "Templates",
    ],
  },
  {
    id: "visibilite",
    name: "Pack Visibilité",
    price: "2199",
    suffix: "€",
    prefix: "À partir de",
    highlight: false,
    features: [
      "Branding complet",
      "4 visuels",
      "Mise en place réseaux",
      "Accompagnement lancement",
    ],
  },
];

const subscriptionTiers = [
  {
    id: "demarreur",
    name: "Démarreur",
    price: "450",
    features: ["3 posts / mois", "Accompagnement léger"],
    cta: "Choisir cette offre",
    highlight: false,
  },
  {
    id: "pro",
    name: "Pro",
    price: "750",
    features: ["6 posts / mois", "Gestion réseaux", "Accompagnement"],
    cta: "Démarrer",
    highlight: true,
    badge: "Le plus choisi",
  },
  {
    id: "premium",
    name: "Premium",
    price: "1200",
    features: ["8 visuels / mois", "Stratégie complète"],
    cta: "Choisir cette offre",
    highlight: false,
  },
];

export default function Branding({ onOpenQuote }) {
  const [subOpen, setSubOpen] = useState(false);
  const [loadingTier, setLoadingTier] = useState(null);

  const handleSubscribe = async (planId) => {
    setLoadingTier(planId);
    try {
      const res = await axios.post(`${API}/checkout/subscription`, {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error("Erreur de redirection vers le paiement.");
    } catch (e) {
      toast.error("Impossible de démarrer le paiement. Merci de réessayer.");
    } finally {
      setLoadingTier(null);
    }
  };

  const handleTierCta = () => {
    setSubOpen(false);
    setTimeout(() => onOpenQuote(), 150);
  };

  return (
    <section
      id="branding"
      data-testid="branding-section"
      className="relative py-24 md:py-32 section-light"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-16 md:mb-20"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — Identité de marque
          </span>
          <h2 className="mt-4 font-outfit text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] leading-[1.05]">
            Votre image, votre identité.
            <br />
            Le branding,{" "}
            <span className="gradient-text font-medium">c&rsquo;est une direction</span>.
          </h2>
          <div className="mt-8 space-y-4 text-[#0A0A0A]/70 text-lg font-light leading-relaxed max-w-2xl">
            <p>
              Un bon branding ne se limite pas à un logo. C&rsquo;est ce qui
              permet à votre marque d&rsquo;être <span className="text-[#0A0A0A] font-normal">reconnue, crédible et mémorable</span>.
            </p>
            <p>
              Aujourd&rsquo;hui, votre image influence directement la décision
              d&rsquo;achat. Une identité forte attire, rassure et donne envie
              de vous faire confiance.
            </p>
            <p className="text-[#0A0A0A]/85">
              Chez EM Group, nous construisons des marques cohérentes et
              impactantes, pensées pour développer votre visibilité et générer
              des résultats.
            </p>
          </div>
        </motion.div>

        {/* 3 Packs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {packs.map((pkg, i) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              whileHover={{ y: -6 }}
              data-testid={`branding-card-${pkg.id}`}
              className={`relative rounded-3xl p-8 md:p-10 transition-all duration-500 ${
                pkg.highlight
                  ? "bg-gradient-to-b from-[#0A0A0A] to-[#141418] text-white border border-[#E52431]/40 glow-red"
                  : "light-card light-card-hover"
              }`}
            >
              {pkg.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                  <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                    {pkg.badge}
                  </div>
                </div>
              )}

              <div
                className={`absolute top-0 left-8 right-8 h-px ${
                  pkg.highlight
                    ? "bg-gradient-to-r from-transparent via-[#E52431] to-transparent"
                    : "bg-gradient-to-r from-transparent via-[#0A0A0A]/15 to-transparent"
                }`}
              />

              <h3
                className={`font-outfit text-xl md:text-2xl font-medium ${
                  pkg.highlight ? "text-white" : "text-[#0A0A0A]"
                }`}
              >
                {pkg.name}
              </h3>

              <div className="mt-6 flex items-baseline gap-2">
                {pkg.prefix && (
                  <span
                    className={`text-sm ${
                      pkg.highlight ? "text-white/50" : "text-[#0A0A0A]/50"
                    }`}
                  >
                    {pkg.prefix}
                  </span>
                )}
                <span
                  className={`font-outfit text-5xl md:text-6xl font-light tracking-tight ${
                    pkg.highlight ? "gradient-text" : "text-[#0A0A0A]"
                  }`}
                >
                  {pkg.price}
                </span>
                <span
                  className={`text-2xl ${
                    pkg.highlight ? "text-white/70" : "text-[#0A0A0A]/70"
                  }`}
                >
                  {pkg.suffix}
                </span>
                <span
                  className={`text-[10px] font-semibold tracking-[0.15em] ml-1 ${
                    pkg.highlight ? "text-white/55" : "text-[#0A0A0A]/55"
                  }`}
                >
                  TTC
                </span>
              </div>

              <ul className="mt-8 space-y-3 min-h-[180px]">
                {pkg.features.map((f) => (
                  <li
                    key={f}
                    className={`flex items-start gap-3 text-sm md:text-base ${
                      pkg.highlight ? "text-white/80" : "text-[#0A0A0A]/70"
                    }`}
                  >
                    <Check
                      size={16}
                      strokeWidth={2}
                      className="text-[#E52431] mt-0.5 flex-shrink-0"
                    />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {pkg.bonus && (
                <div
                  className={`mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-semibold ${
                    pkg.highlight
                      ? "bg-white/10 text-white"
                      : "bg-[#E52431]/10 text-[#E52431]"
                  }`}
                >
                  <Sparkles size={13} />
                  Bonus : {pkg.bonus}
                </div>
              )}

              <button
                data-testid={`branding-cta-${pkg.id}`}
                onClick={onOpenQuote}
                className={`mt-8 w-full rounded-full py-4 font-semibold text-sm uppercase tracking-[0.15em] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 ${
                  pkg.highlight
                    ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                    : "bg-[#0A0A0A] text-white hover:bg-[#E52431]"
                }`}
              >
                Je choisis cette offre
                <ArrowRight size={14} />
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-6 text-center text-xs text-[#0A0A0A]/40 font-light">
          Tous les prix affichés sont en TTC.
        </p>

        {/* Subscription */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          data-testid="branding-subscription"
          className="mt-10 relative rounded-3xl overflow-hidden"
        >
          <div
            className="relative p-8 md:p-12"
            style={{
              background:
                "linear-gradient(135deg, #0A0A0A 0%, #160812 35%, #0B1030 75%, #0A0A0A 100%)",
            }}
          >
            <div className="absolute -top-32 -left-20 w-[400px] h-[400px] rounded-full bg-[#E52431]/25 blur-[100px]" />
            <div className="absolute -bottom-32 -right-20 w-[400px] h-[400px] rounded-full bg-[#1F2C6E]/35 blur-[100px]" />

            <div className="relative z-10 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-7 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full gradient-bg text-[10px] uppercase tracking-[0.2em] font-semibold mb-5">
                  <Repeat size={12} /> Offre récurrente
                </div>
                <h3 className="font-outfit text-3xl md:text-4xl lg:text-5xl font-light tracking-tight leading-[1.1]">
                  Continuer à développer{" "}
                  <span className="gradient-text font-medium">
                    votre visibilité
                  </span>
                </h3>
                <p className="mt-5 text-white/65 font-light text-lg max-w-xl">
                  Une fois votre marque construite, maintenez la cadence et
                  transformez votre audience en clients.
                </p>

                <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 max-w-md">
                  {["3 posts / mois", "Gestion réseaux", "Optimisation", "Accompagnement"].map((f) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 text-white/85 text-sm md:text-base"
                    >
                      <Check
                        size={16}
                        strokeWidth={2}
                        className="text-[#E52431] mt-0.5 flex-shrink-0"
                      />
                      <span className="font-light">{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="lg:col-span-5">
                <div className="rounded-2xl bg-white/5 backdrop-blur-xl border border-white/10 p-8">
                  <div className="text-xs uppercase tracking-wider text-white/55">
                    À partir de
                  </div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-outfit text-6xl md:text-7xl font-light tracking-tight text-white">
                      450
                    </span>
                    <span className="text-2xl text-white/70">€</span>
                    <span className="text-[11px] font-semibold tracking-[0.15em] text-white/60 ml-0.5">
                      TTC
                    </span>
                    <span className="text-lg text-white/55 ml-1">/mois</span>
                  </div>

                  <button
                    data-testid="branding-subscription-cta"
                    onClick={() => handleSubscribe("demarreur")}
                    disabled={loadingTier === "demarreur"}
                    className="mt-6 w-full rounded-full gradient-bg text-white font-semibold py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_40px_rgba(229,36,49,0.5)] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {loadingTier === "demarreur"
                      ? "Redirection..."
                      : "Démarrer l\u2019abonnement"}
                    {loadingTier !== "demarreur" && <ArrowRight size={14} />}
                  </button>
                  <button
                    data-testid="branding-subscription-details"
                    onClick={() => setSubOpen(true)}
                    className="mt-3 w-full rounded-full bg-white/5 border border-white/15 text-white font-medium py-3 text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300 inline-flex items-center justify-center gap-2"
                  >
                    Voir les détails des 3 formules
                  </button>
                  <p className="mt-3 text-center text-xs text-white/50 font-light">
                    ⚡ Sans engagement · Résiliable à tout moment
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Subscription Tiers Dialog */}
      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent
          data-testid="subscription-dialog"
          className="bg-[#0A0A0A] border border-white/10 text-white max-w-5xl max-h-[90vh] overflow-y-auto rounded-3xl p-0"
        >
          <div className="relative h-1 gradient-bg" />
          <div className="p-8 md:p-10">
            <DialogHeader className="text-left">
              <DialogTitle className="font-outfit text-2xl md:text-3xl font-light">
                Nos{" "}
                <span className="gradient-text font-medium">formules mensuelles</span>
              </DialogTitle>
              <DialogDescription className="text-white/55 mt-1">
                Choisissez la cadence qui correspond à vos ambitions.
              </DialogDescription>
            </DialogHeader>

            <AnimatePresence>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
                {subscriptionTiers.map((tier, i) => (
                  <motion.div
                    key={tier.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    data-testid={`subscription-tier-${tier.id}`}
                    className={`relative rounded-2xl p-7 border transition-all duration-500 ${
                      tier.highlight
                        ? "bg-gradient-to-b from-[#1a0f12] to-[#141418] border-[#E52431]/50 glow-red"
                        : "bg-[#141418] border-white/10 hover:border-white/25"
                    }`}
                  >
                    {tier.highlight && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20 whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5 px-4 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] gradient-bg text-white font-semibold shadow-[0_8px_20px_rgba(229,36,49,0.35)]">
                          <Flame size={12} /> {tier.badge}
                        </div>
                      </div>
                    )}

                    <div className="text-xs uppercase tracking-[0.2em] text-white/50">
                      {tier.name}
                    </div>

                    <div className="mt-4 flex items-baseline gap-1">
                      <span
                        className={`font-outfit text-5xl font-light tracking-tight ${
                          tier.highlight ? "gradient-text" : "text-white"
                        }`}
                      >
                        {tier.price}
                      </span>
                      <span className="text-xl text-white/70">€</span>
                      <span className="text-[10px] font-semibold tracking-[0.15em] text-white/55 ml-0.5">
                        TTC
                      </span>
                      <span className="text-sm text-white/50 ml-1">/mois</span>
                    </div>

                    <ul className="mt-6 space-y-2.5 min-h-[110px]">
                      {tier.features.map((f) => (
                        <li
                          key={f}
                          className="flex items-start gap-3 text-sm text-white/80"
                        >
                          <Check
                            size={15}
                            strokeWidth={2}
                            className="text-[#E52431] mt-0.5 flex-shrink-0"
                          />
                          <span className="font-light">{f}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      data-testid={`subscription-cta-${tier.id}`}
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={loadingTier === tier.id}
                      className={`mt-6 w-full rounded-full py-3.5 font-semibold text-xs uppercase tracking-[0.15em] transition-all duration-300 btn-shine inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed ${
                        tier.highlight
                          ? "gradient-bg text-white hover:shadow-[0_0_40px_rgba(229,36,49,0.5)]"
                          : "bg-white text-[#0A0A0A] hover:bg-[#E52431] hover:text-white"
                      }`}
                    >
                      {loadingTier === tier.id ? "Redirection..." : tier.cta}
                      {loadingTier !== tier.id && <ArrowRight size={13} />}
                    </button>
                  </motion.div>
                ))}
              </div>
            </AnimatePresence>

            <button
              data-testid="subscription-custom"
              onClick={handleTierCta}
              className="mt-6 w-full rounded-full bg-white/5 border border-white/15 text-white font-medium py-3 text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300"
            >
              Demander un devis sur-mesure
            </button>
            <p className="mt-3 text-center text-xs text-white/50 font-light">
              ⚡ Sans engagement · Résiliable à tout moment
            </p>
            <p className="mt-1 text-center text-[11px] text-white/40 font-light">
              Tous les prix affichés sont en TTC.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
